import React, { memo, useState } from 'react';
import { BarChart2, ChevronDown, ChevronRight, Download, ExternalLink, Trash2 } from 'lucide-react';
import type { OrgOlderAttemptRow, OrgRunsSortBy, OrgRunsSortState, OrgTestRunRow } from '../../../../types/controlTestRun.ts';
import { FINISHED, periodLabel, StatusCell, ResultsCell, canDeleteAttempts } from '../../../../components/test-runs/runDisplay.js';
import { isPopulationNotRequired } from '../../../../api/controlTestRuns.js';
import { DataTableShell } from './DataTableShell.tsx';
import { SortableHeaderCell } from './SortableHeaderCell.tsx';
import {
  bodyCellClass,
  bodyRowClass,
  headerCellCenterClass,
  headerCellLeftClass,
  theadClass,
  actionIconButtonClass,
  actionIconButtonDangerClass,
} from './styles.ts';

export type TableProps = {
  rows: OrgTestRunRow[];
  sort: OrgRunsSortState;
  onSortChange: (key: OrgRunsSortBy) => void;
  onResultsClick: (run: OrgTestRunRow) => void;
  onOpenControlClick: (run: OrgTestRunRow) => void;
  onDownloadClick: (run: OrgTestRunRow) => void;
  /** Omitted when the user cannot delete (locked audit year). */
  onDeleteClick?: (run: OrgTestRunRow) => void;
};

const isFinished = (run: OrgTestRunRow): boolean => FINISHED.includes(String(run.status || '').toLowerCase());

/**
 * A row deletes every attempt of its rerun chain, so it can be deleted only when no attempt is in
 * review or signed off — same rule as the control's Test Runs tab.
 */
export const canDeleteRun = (run: Pick<OrgTestRunRow, 'status'> & { olderAttempts?: Pick<OrgOlderAttemptRow, 'status'>[] }): boolean =>
  canDeleteAttempts([run.status, ...(run.olderAttempts || []).map((a) => a.status)]);

/** An earlier attempt as a row the Results / Download actions accept. */
const attemptAsRow = (run: OrgTestRunRow, a: OrgOlderAttemptRow): OrgTestRunRow =>
  ({ ...run, ...a, olderAttempts: [], attempts: 1, attemptIds: [a.executionId] }) as OrgTestRunRow;

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

const sampleLabel = (run: OrgTestRunRow): string | null => {
  if (isPopulationNotRequired(run)) return 'evidence only';
  const n = run.total_sample_size;
  return n ? `${n} sample${n === 1 ? '' : 's'}` : null;
};

type TestRunRowProps = Omit<TableProps, 'rows' | 'sort' | 'onSortChange'> & { run: OrgTestRunRow };

const TestRunRow = memo(
  ({ run, onResultsClick, onOpenControlClick, onDownloadClick, onDeleteClick }: TestRunRowProps): React.ReactElement => {
    const [expanded, setExpanded] = useState(false);
    const finished = isFinished(run);
    const signedOff = String(run.status || '').toLowerCase() === 'signed_off';
    const subtitle = [
      `RUN-${run.test_run_number ?? '?'}`,
      run.test_type ? String(run.test_type).toUpperCase() : null,
      sampleLabel(run),
    ]
      .filter(Boolean)
      .join(' · ');
    const hasOlder = run.olderAttempts.length > 0;
    const people = [
      run.preparerName ? `P: ${run.preparerName}` : null,
      run.reviewerName ? `R: ${run.reviewerName}` : null,
    ]
      .filter(Boolean)
      .join(' · ');

    return (
      <>
      <tr className={bodyRowClass}>
        <td className={`${bodyCellClass} align-top`}>
          <span className="font-mono text-sm font-medium text-gray-900 block">{run.controlId}</span>
          {run.controlTitle && <span className="text-xs text-gray-500 block mt-0.5">{run.controlTitle}</span>}
          {run.controlType && (
            <span className="inline-flex mt-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200 capitalize">
              {run.controlType}
            </span>
          )}
        </td>
        <td className={`${bodyCellClass} align-top`}>
          <span className="text-sm font-medium text-gray-800 block">{run.test_run_name || 'Untitled'}</span>
          <span className="text-xs text-gray-500 block mt-0.5">
            {subtitle}
            {hasOlder && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                title={expanded ? 'Hide earlier attempts' : 'Show earlier attempts'}
                className="inline-flex items-center gap-0.5 ml-1 text-gray-500 hover:text-gray-800"
              >
                · {run.attempts}×
                {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              </button>
            )}
          </span>
        </td>
        <td className={`${bodyCellClass} align-top text-sm text-gray-600`}>{periodLabel(run)}</td>
        <td className={`${bodyCellClass} align-top`}>
          <StatusCell
            status={run.status}
            workflowStatus={run.workflow_status}
            running={false}
            onRunningClick={undefined}
            populationNotRequired={isPopulationNotRequired(run)}
          />
          {people && <span className="text-xs text-gray-500 block mt-1">{people}</span>}
        </td>
        <td className={`${bodyCellClass} align-top`}>
          <ResultsCell run={run} />
        </td>
        <td className={`${bodyCellClass} align-top text-sm text-gray-600`}>{formatDate(run.created_at)}</td>
        <td className={`${bodyCellClass} align-top`}>
          <div className="flex items-center justify-center gap-1">
            {finished && (
              <button
                type="button"
                onClick={() => onResultsClick(run)}
                className={actionIconButtonClass}
                title={signedOff ? 'View results (signed off)' : 'Results, samples & sign-off'}
                aria-label="Results"
              >
                <BarChart2 className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => onOpenControlClick(run)}
              className={actionIconButtonClass}
              title="Open in control's Test Runs tab"
              aria-label="Open in control"
            >
              <ExternalLink className="h-4 w-4" />
            </button>
            {finished && (
              <button
                type="button"
                onClick={() => onDownloadClick(run)}
                className={actionIconButtonClass}
                title="Download working paper"
                aria-label="Download working paper"
              >
                <Download className="h-4 w-4" />
              </button>
            )}
            {onDeleteClick && canDeleteRun(run) && (
              <button
                type="button"
                onClick={() => onDeleteClick(run)}
                className={actionIconButtonDangerClass}
                title="Delete"
                aria-label="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded &&
        run.olderAttempts.map((a) => {
          const aFinished = FINISHED.includes(String(a.status || '').toLowerCase());
          const asRow = attemptAsRow(run, a);
          return (
            <tr key={a.executionId} className="border-b bg-gray-50/60 text-xs">
              <td className={`${bodyCellClass} pl-8 text-gray-500`}>↳ Earlier attempt</td>
              <td className={bodyCellClass}>
                <span className="text-gray-700 block">{a.test_run_name || 'Untitled'}</span>
                <span className="text-gray-500">RUN-{a.test_run_number}</span>
              </td>
              <td className={`${bodyCellClass} text-gray-500`}>{periodLabel(run)}</td>
              <td className={bodyCellClass}>
                <StatusCell status={a.status} workflowStatus={a.workflow_status} running={false} onRunningClick={undefined} />
              </td>
              <td className={bodyCellClass}>
                <ResultsCell run={a} />
              </td>
              <td className={`${bodyCellClass} text-gray-500`}>{formatDate(a.created_at)}</td>
              <td className={bodyCellClass}>
                {aFinished && (
                  <div className="flex items-center justify-center gap-1">
                    <button type="button" onClick={() => onResultsClick(asRow)} className={actionIconButtonClass}
                      title="Results of this attempt" aria-label="Results of this attempt">
                      <BarChart2 className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => onDownloadClick(asRow)} className={actionIconButtonClass}
                      title="Download this attempt's working paper" aria-label="Download this attempt's working paper">
                      <Download className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </>
    );
  }
);

TestRunRow.displayName = 'TestRunRow';

const SORTABLE: Record<string, OrgRunsSortBy> = {
  Control: 'control_id',
  'Test run': 'run_number',
  Status: 'workflow_status',
  Created: 'created_at',
};

const sortHeader = (label: string, sort: OrgRunsSortState, onSortChange: (key: OrgRunsSortBy) => void) => (
  <SortableHeaderCell key={label} label={label} columnKey={SORTABLE[label]} sort={sort} onSortChange={onSortChange} />
);

export const TestRunsTable = ({ rows, sort, onSortChange, ...actions }: TableProps): React.ReactElement => (
  <DataTableShell>
    <thead className={theadClass}>
      <tr>
        {sortHeader('Control', sort, onSortChange)}
        {sortHeader('Test run', sort, onSortChange)}
        <th className={headerCellLeftClass}>Period</th>
        {sortHeader('Status', sort, onSortChange)}
        <th className={headerCellLeftClass}>Results</th>
        {sortHeader('Created', sort, onSortChange)}
        <th className={headerCellCenterClass}>Actions</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((run) => (
        <TestRunRow key={run.executionId} run={run} {...actions} />
      ))}
    </tbody>
  </DataTableShell>
);
